import React, { useState, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useRealtimeDrivers } from 'Core/hooks';
import { useIntl, FormattedNumber } from 'react-intl';
import moment from 'moment';

//Helpers
import {
    GetFormatForMoment,
    statusPill
} from '../../../../core/hooks/utils';

//Components
import {
    InputNumeric,
    DatePicker,
    FileUpload,
    TextArea,
    Button,
    Modal,
    Form,
} from '@gpstrackit/gtc-react-controls';
import { NotificationContainer } from "Components/Notifications";
import { NotificationManager } from 'Components/Notifications';
import { Checkbox } from 'Components';
import { Grid } from '@mui/material';

//Actions
import {
    maintenanceCDSTCreateUpdateDriverScheduledTaskLog,
    maintenanceCDSTGetDriverScheduledTask,
    reset,
} from './redux/slice';
import {
    uploadAttachment,
    catalogLoad
} from 'Redux/actions';

//Styles
import './styles.scss';

const CompleteDriverScheduledTask = (props) => {
    const { refresh } = props;
    const { messages } = useIntl();
    const dispatch = useDispatch();
    const formRef = Form.useForm();

    let path = location.pathname.split("/");
    const id = path[path.length - 1];

    //Redux States
    const {
        completeDriverScheduledTask,
        driverScheduledTask,
        driverName,
        loading,
        tasks,
        error,
        open,
    } = useSelector(state => state.maintenanceCompleteDriverScheduledTaskRedux);
    const { uploadedAttachment, loadingUploadFile } = useSelector(state => state.commomRedux);
    const drivers = useRealtimeDrivers({ trackFields: ['id', 'name', 'externalId', 'disabled', 'tags'] });
    const { user } = useSelector(state => state.securityRedux);

    //Local States
    const [formData, setFormData] = useState({ attachments: [], reschedule: true });
    const [driver, setDriver] = useState(null);
    const [currentError, setCurrentError] = useState(null);

    useEffect(() => {
        if (open && id) {
            if (!drivers?.length)
                dispatch(catalogLoad(['drivers']));
            else {
                let currentDriver = drivers.find((e) => e?.driverId == id)
                currentDriver && setDriver(currentDriver);
            }
        }
    }, [open, drivers])

    useEffect(() => {
        if (open) {
            setFormData({ ...formData })
            dispatch(maintenanceCDSTGetDriverScheduledTask({ id: tasks?.id }))
        }
    }, [open])

    useEffect(() => {
        if (error) {
            NotificationManager.error(
                error?.error || messages['errorMessage'],
                messages['error'],
                3000,
                null,
                null,
                'error',
            );
            dispatch(reset({ error: null }))
        }
    }, [error])

    useEffect(() => {
        if (completeDriverScheduledTask?.id && open) {
            NotificationManager.success(
                messages['success'],
                messages['completeDriverScheduledTasks_taskCompleted'],
                3000,
                null,
                null,
                'success'
            );
            dispatch(maintenanceCDSTGetDriverScheduledTask({ id: tasks?.id }))
            refresh && refresh();
        }
    }, [completeDriverScheduledTask])

    const isToday = (date) => {
        const today = new Date()
        return date.getDate() === today.getDate() && date.getMonth() === today.getMonth() && date.getFullYear() === today.getFullYear();
    };

    const handleClose = () => {
        setFormData({ attachments: [], reschedule: true });
        dispatch(reset());
        setDriver(null);
    }

    const completeScheduledDriverTask = () => {
        if (!formData.logDate) {
            setCurrentError(`${messages['mapLandmarkImport_fieldsRequied']} *`)
        } else {
            setCurrentError('')
            let attachments = formData?.attachments.length ? formData?.attachments.map((item) => item?.id) : []
            let data = {
                scheduledTaskId: driverScheduledTask ? driverScheduledTask.id : null,
                logDate: formData.logDate.toISOString(),
                actualCost: formData.actualCost ? parseFloat(formData.actualCost) : 0,
                notes: formData.notes || null,
                completed: !formData.reschedule,
                nextDueDate: formData.nextDueDate.toISOString(),
                fileIds: attachments
            };
            dispatch(maintenanceCDSTCreateUpdateDriverScheduledTaskLog(data))
        }
    }

    return (
        <Modal
            title={`${driverName || driver?.name || ''} - ${tasks?.driverTaskName}`}
            open={open}
            handleClose={handleClose}
            position="right"
            size="small"
            loading={loading}
            footer={(
                <div>
                    {
                        !completeDriverScheduledTask?.id &&
                        <Button
                            onClick={handleClose}
                            variant="secondary"
                            style={{ marginRight: "5px" }}
                        >
                            {messages['cancel']}
                        </Button>
                    }
                    {
                        completeDriverScheduledTask?.id &&
                        <Button
                            onClick={() => handleClose()}
                            variant="primary"
                        >
                            {messages['close']}
                        </Button>
                    }
                    {
                        !completeDriverScheduledTask?.id &&
                        <Button
                            onClick={() => formRef.submit()}
                            variant="primary"
                        >
                            {formData.reschedule ? messages['completeDriverScheduledTasks_completeSchedule'] : messages['completeDriverScheduledTasks_complete']}
                        </Button>
                    }
                </div>
            )}
        >
            <div style={{ padding: '10px 25px' }}>
                <div style={{ display: 'flex', justifyContent: 'center', width: '100%', padding: 10 }}>
                    {currentError && <small className="select-error text-danger">{currentError}</small>}
                </div>
                <Grid container>
                    <Form
                        ref={formRef.ref}
                        onValidSubmit={(_values) => {
                            if (_values.logDate && formData.reschedule) completeScheduledDriverTask()
                        }}
                        onSubmit={(_values) => {
                            if (_values.logDate && !formData.reschedule) completeScheduledDriverTask()
                        }}
                        className='complete-driver-scheduled-tasks-form'
                    >
                        <Form.Item
                            label={messages['completeDriverScheduledTasks_status']}
                        >
                            <>{driverScheduledTask?.status && statusPill(driverScheduledTask)}</>
                        </Form.Item>
                        {
                            completeDriverScheduledTask?.logDate &&
                            <Form.Item
                                label={messages['completeDriverScheduledTasks_completed']}
                            >
                                <>{moment(new Date(completeDriverScheduledTask?.logDate)).format(GetFormatForMoment(user.dateformat))}</>
                            </Form.Item>
                        }
                        {
                            driverScheduledTask && completeDriverScheduledTask &&
                            <Form.Item
                                label={messages['completeDriverScheduledTasks_repeatEvery']}
                            >
                                <>{driverScheduledTask?.frequency} {messages[driverScheduledTask?.timeUnit]}</>
                            </Form.Item>
                        }
                        {
                            driverScheduledTask && !completeDriverScheduledTask &&
                            <>
                                <Form.Item
                                    label={messages['completeDriverScheduledTasks_dueDate']}
                                >
                                    <>{driverScheduledTask?.dueDate && moment(new Date(driverScheduledTask.dueDate)).format(GetFormatForMoment(user.dateformat))}</>
                                </Form.Item>
                                <Form.Item
                                    label={messages['completeDriverScheduledTasks_estimatedCost']}
                                >
                                    <><FormattedNumber value={driverScheduledTask?.estimatedCost || 0} style="currency" currency="USD" /></>
                                </Form.Item>
                            </>
                        }
                        <Form.Item
                            name="actualCost"
                            label={messages['completeDriverScheduledTasks_actualCost']}
                            rules={[{
                                max: 99999999999,
                                message: `${messages['max']}: 99999999999`
                            }]}
                        >
                            <InputNumeric
                                id="actualCost"
                                type="currency"
                                placeholder={messages['completeDriverScheduledTasks_actualCost']}
                                onChange={(value) => {
                                    value = value.replace(/[_-]/g, "");
                                    setFormData({ ...formData, actualCost: parseFloat(value) });
                                    setTimeout(() => { formRef.setValues({ actualCost: value }) }, 50);
                                }}
                                maxLength={11}
                                isDisabled={completeDriverScheduledTask?.id}
                                decimalPlaces={2}
                            />
                        </Form.Item>
                        {
                            !completeDriverScheduledTask &&
                            <Form.Item
                                name="logDate"
                                label={messages['completeDriverScheduledTasks_completedDate']}
                                rules={[{ required: true, message: 'required_field' }]}
                            >
                                <DatePicker
                                    id="logDate"
                                    showTimePicker
                                    calendarPosition="bottom-end"
                                    placeholder={messages['completeDriverScheduledTasks_completedDate']}
                                    format={GetFormatForMoment(user.dateformat)}
                                    maxDate={new Date()}
                                    onChange={(value) => {
                                        if (moment(value).isAfter(moment(), 'minutes')) {
                                            return NotificationManager.warning(
                                                messages['scheduleDriverTask_wait'],
                                                messages['scheduleDriverTask_invalidTime'],
                                                3000,
                                                null,
                                                null,
                                                'success',
                                            );
                                        }
                                        let nextDueDate = "";
                                        let logDate = value;
                                        if (driverScheduledTask) {
                                            const { timeUnit, frequency } = driverScheduledTask;
                                            let newDate = new Date(value.getTime());
                                            if (timeUnit === 'Hours' || timeUnit === 'Hour') {
                                                const now = moment().add(frequency, 'hours');
                                                if (isToday(new Date(value))) logDate = new Date();
                                                nextDueDate = now;
                                            } else if (timeUnit === 'Days' || timeUnit === 'Day') {
                                                nextDueDate = newDate.setDate(newDate.getDate() + parseInt(frequency));
                                            } else if (timeUnit === 'Months' || timeUnit === 'Month') {
                                                nextDueDate = newDate.setMonth((newDate.getMonth()) + parseInt(frequency));
                                            }
                                            if (moment(nextDueDate).isBefore(moment(), 'days')) {
                                                return NotificationManager.warning(
                                                    messages['scheduleDriverTask_wait'],
                                                    messages['scheduleDriverTask_next_due_invalid_time'],
                                                    3000,
                                                    null,
                                                    null,
                                                    'success',
                                                );
                                            }
                                        }
                                        setFormData({ ...formData, logDate, nextDueDate: nextDueDate ? new Date(nextDueDate) : "" });
                                        setTimeout(() => {
                                            formRef.setValues({ ...formData, nextDueDate: nextDueDate ? new Date(nextDueDate) : "" })
                                        }, 100);
                                    }}
                                />
                            </Form.Item>
                        }

                        <Form.Item
                            name="attachments"
                            label={messages['completeDriverScheduledTasks_attachments']}
                        >
                            <FileUpload
                                id="attachments"
                                placeholder={messages['completeMaintenanceService_select_files']}
                                types="image/*,.txt,.doc,.docx,.pdf,.csv,application/vnd.ms-excel,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
                                maxSize={4}
                                maxSizeMessage={`${messages['max']}: 4MB`}
                                helpText={messages['attachmentsFilesAllowed']}
                                uploadAttachment={(data) => { dispatch(uploadAttachment(data)) }}
                                uploadedAttachment={uploadedAttachment}
                                loadingUploadFile={loadingUploadFile}
                                onChange={(value) => setFormData({ ...formData, attachments: value })}
                                isDisabled={completeDriverScheduledTask?.id}
                            />
                        </Form.Item>
                        <Form.Item
                            name="notes"
                            label={messages['completeDriverScheduledTasks_notes']}
                        >
                            <TextArea
                                id="notes"
                                placeholder={messages['completeDriverScheduledTasks_notes']}
                                value={formData.notes}
                                onChange={(value) => setFormData({ ...formData, notes: value })}
                                maxlength={500}
                                isDisabled={completeDriverScheduledTask?.id}
                            />
                        </Form.Item>
                        {
                            !completeDriverScheduledTask &&
                            <>
                                <div className="complete-driver-scheduled-checkbox">
                                    <Checkbox
                                        id="reschedule"
                                        value={formData.reschedule}
                                        checked={formData.reschedule}
                                        label={messages['completeDriverScheduledTasks_rescheduleTask']}
                                        onChange={(value) => {
                                            setFormData({ ...formData, reschedule: value })
                                        }}
                                    />
                                </div>
                                {
                                    formData.reschedule &&
                                    <Form.Item
                                        name="nextDueDate"
                                        label={messages['completeDriverScheduledTasks_nextDueDate']}
                                        rules={[
                                            {
                                                required: formData?.reschedule && formData?.logDate,
                                                message: messages['required_field']
                                            }
                                        ]}
                                    >
                                        <DatePicker
                                            id="nextDueDate"
                                            minDate={new Date()}
                                            showTimePicker
                                            calendarPosition="bottom-end"
                                            format={GetFormatForMoment(user.dateformat)}
                                            onChange={(value) => {
                                                if (moment(value).isBefore(moment(), 'days')) {
                                                    return NotificationManager.warning(
                                                        messages['scheduleDriverTask_wait'],
                                                        messages['scheduleDriverTask_next_due_invalid_time'],
                                                        3000,
                                                        null,
                                                        null,
                                                        'success',
                                                    );
                                                }
                                                setFormData({ ...formData, nextDueDate: value })
                                            }}
                                            isDisabled={!formData.logDate}
                                        />
                                    </Form.Item>
                                }
                            </>
                        }
                    </Form>
                </Grid>
            </div>
            <NotificationContainer />
        </Modal>
    )
}

export default CompleteDriverScheduledTask